import { useCallback, useEffect, useState } from 'react';
import { api } from '../../api/client';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import StatusPill from '../../components/StatusPill';

type Lead = { id: string; company_name: string; city: string | null; score: number | null; status: string; stage: string; assigned_to: string | null };
type Seller = { id: string; fullName: string; role: string; isActive: boolean };

export default function Capturados() {
  const { user } = useAuth();
  const [pros, setPros] = useState<Lead[]>([]);   // estágio prospecto (da prospecção)
  const [cap, setCap] = useState<Lead[]>([]);    // capturados (em tratamento)
  const [sellers, setSellers] = useState<Seller[]>([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [p, c, u] = await Promise.all([
      api.get<{ leads: Lead[] }>('/leads?stage=prospecto'),
      api.get<{ leads: Lead[] }>('/leads?stage=capturado'),
      api.get<{ users: Seller[] }>('/users'),
    ]);
    setPros(p.leads);
    setCap(c.leads);
    setSellers(u.users.filter((x) => x.role === 'vendedor' && x.isActive));
  }, []);
  useEffect(() => { load(); }, [load]);

  async function promover(id: string, proximo: 'capturado' | 'lead') {
    setBusy(true);
    try {
      await api.patch(`/leads/${id}`, { stage: proximo });
      toast.success(proximo === 'capturado' ? 'Enviado para Capturados.' : 'Enviado para Leads (em potencial).');
      await load();
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Falha');
    } finally {
      setBusy(false);
    }
  }

  async function direcionar(id: string, sellerId: string) {
    setBusy(true);
    try {
      await api.patch(`/leads/${id}`, { assigned_to: sellerId });
      const who = sellers.find((s) => s.id === sellerId);
      toast.success(`Direcionado: ${who?.fullName ?? 'vendedor'}.`);
      await load();
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Falha');
    } finally {
      setBusy(false);
    }
  }

  const Card = ({ l }: { l: Lead }) => (
    <div className="rounded-lg border border-[var(--border-strong)] p-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{l.company_name}</span>
        <StatusPill status={l.status} />
        {l.score != null && <span className="rounded bg-[var(--bg-subtle)] px-1.5 py-0.5 text-[10px] font-semibold">score {l.score}</span>}
        <span className="text-xs text-[var(--text-dim)]">{l.city ?? ''}</span>
      </div>
      <button onClick={() => promover(l.id, 'capturado')} disabled={busy}
        className="mt-2 h-8 rounded-md border border-[var(--border-strong)] px-3 text-xs font-medium hover:bg-[var(--bg-subtle)] disabled:opacity-50">
        → Capturados
      </button>
      {user?.role !== 'vendedor' && (
        <select
          value={l.assigned_to ?? ''}
          onChange={(e) => e.target.value && direcionar(l.id, e.target.value)}
          disabled={busy}
          className="mt-2 ml-2 h-8 rounded-md border border-[var(--border-strong)] bg-white px-1 text-xs"
        >
          <option value="">Direcionar...</option>
          {sellers.map((s) => <option key={s.id} value={s.id}>{s.fullName}</option>)}
        </select>
      )}
    </div>
  );

  return (
    <div className="max-w-5xl">
      <header>
        <p className="text-xs font-semibold tracking-widest text-[var(--text-dim)] uppercase">Capturados</p>
        <h1 className="mt-1 text-xl font-semibold tracking-tight">Prospectos tratados ({pros.length + cap.length})</h1>
        <p className="mt-1 text-sm text-[var(--text-dim)]">
          Os clientes prospectados na Prospecção entram aqui. Trate o lead, envie para Capturados e, quando estiver potencial, encaminhe para a aba Leads.
        </p>
      </header>

      <section className="mt-6">
        <h2 className="text-sm font-semibold">Da prospecção — aguardando tratamento ({pros.length})</h2>
        <div className="mt-2 grid gap-2 md:grid-cols-2">
          {pros.length === 0 && <p className="text-sm text-[var(--text-dim)]">Nenhum prospecto novo. Busque empresas na aba Prospecção e importe — elas chegam aqui.</p>}
          {pros.map((l) => <Card key={l.id} l={l} />)}
        </div>
      </section>

      <section className="mt-8">
        <h2 className="text-sm font-semibold">Capturados — em tratamento ({cap.length})</h2>
        <div className="mt-2 grid gap-2 md:grid-cols-2">
          {cap.length === 0 && <p className="text-sm text-[var(--text-dim)]">Nenhum capturado ainda.</p>}
          {cap.map((l) => (
            <div key={l.id} className="rounded-lg border border-[var(--border-strong)] p-3 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{l.company_name}</span>
                <StatusPill status={l.status} />
                <span className="text-xs text-[var(--text-dim)]">{l.city ?? ''}</span>
              </div>
              <button onClick={() => promover(l.id, 'lead')} disabled={busy}
                className="mt-2 h-8 rounded-md bg-[var(--accent)] px-3 text-xs font-semibold text-white hover:bg-[#c40a20] disabled:opacity-50">
                → Leads (em potencial)
              </button>
              {user?.role !== 'vendedor' && (
                <select
                  value={l.assigned_to ?? ''}
                  onChange={(e) => e.target.value && direcionar(l.id, e.target.value)}
                  disabled={busy}
                  className="mt-2 ml-2 h-8 rounded-md border border-[var(--border-strong)] bg-white px-1 text-xs"
                >
                  <option value="">Direcionar...</option>
                  {sellers.map((s) => <option key={s.id} value={s.id}>{s.fullName}</option>)}
                </select>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}